import { useLanguage } from "@/context/language"
import { formatDate } from "@/utils/date"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { createContext, createMemo, For, onCleanup, Show, useContext, type JSX } from "solid-js"
import { createStore } from "solid-js/store"

const labels: Record<string, string> = {
  accounting_records: "cowork.accounting.extractions.accountingFacts",
  facts: "cowork.accounting.review.documentFacts",
  date: "cowork.enterprise.audit.date",
  document_date: "cowork.accounting.documents.column.date",
  invoice_date: "cowork.accounting.documents.column.date",
  due_date: "cowork.accounting.review.dueDate",
  currency: "cowork.accounting.documents.column.currency",
  counterparty: "cowork.accounting.documents.column.counterparty",
  counterparty_name: "cowork.accounting.documents.column.counterparty",
  company_name: "cowork.enterprise.companies.namePlaceholder",
  name: "cowork.accounting.extractions.fact.name",
  vat_number: "cowork.enterprise.companies.vatNumber",
  counterparty_vat_number: "cowork.enterprise.companies.vatNumber",
  supplier_vat_number: "cowork.accounting.review.supplierVatNumber",
  buyer_vat_number: "cowork.accounting.review.buyerVatNumber",
  gross_amount: "cowork.accounting.documents.column.gross",
  net_amount: "cowork.accounting.review.net",
  vat_amount: "cowork.accounting.review.vat",
  vat_rate: "cowork.accounting.review.vatRate",
  amount: "cowork.accounting.payments.amount",
  description: "cowork.accounting.ledger.column.description",
  quantity: "cowork.accounting.review.quantity",
  lines: "cowork.accounting.extractions.lineItems",
  line_items: "cowork.accounting.extractions.lineItems",
  source_refs: "cowork.accounting.review.sourceReferences",
  source_ref: "cowork.accounting.tax.preview.sourceReference",
  reference: "cowork.accounting.payments.reference",
  references: "cowork.accounting.extractions.section.references",
  source: "cowork.enterprise.files.source",
  evidence_refs: "cowork.accounting.review.sourceReferences",
  raw_text: "cowork.accounting.review.documentContent",
  event_type: "cowork.enterprise.audit.eventType",
  document_type: "cowork.accounting.extractions.fact.documentType",
  invoice_number: "cowork.accounting.documents.column.number",
  document_number: "cowork.accounting.documents.column.number",
  seller: "cowork.accounting.extractions.party.seller",
  buyer: "cowork.accounting.extractions.party.buyer",
  iban: "cowork.accounting.extractions.fact.iban",
  expense_receipt: "cowork.accounting.documentType.receipt",
  receipt: "cowork.accounting.documentType.receipt",
  invoice: "cowork.accounting.documentType.invoice",
  purchase_invoice: "cowork.accounting.documentType.purchase_invoice",
  sales_invoice: "cowork.accounting.documentType.sales_invoice",
  bank_statement: "cowork.accounting.documentType.bank_statement",
}

const FieldLabels = createContext<Record<string, string>>({})

export function ExtractionFieldLabels(props: { labels?: Record<string, string>; children: JSX.Element }) {
  return <FieldLabels.Provider value={props.labels ?? {}}>{props.children}</FieldLabels.Provider>
}

const supportingFields = new Set([
  "reference",
  "references",
  "source",
  "sources",
  "source_refs",
  "source_ref",
  "evidence_refs",
  "provenance",
  "source_workbook",
  "raw_extraction",
  "source_text",
  "raw_text",
  "extracted_text",
])

function isSupportingField(key: string) {
  return supportingFields.has(key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase())
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

function isField(value: unknown): boolean {
  return (
    value === null ||
    typeof value !== "object" ||
    (Array.isArray(value) && value.every((item) => item === null || typeof item !== "object"))
  )
}

function fieldLabel(key: string, translate: (key: string) => string, contractLabels: Record<string, string> = {}) {
  const normalized = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
  if (contractLabels[normalized]) return translate(contractLabels[normalized])
  if (labels[normalized]) return translate(labels[normalized])
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b(vat|iban|id|url|bic)\b/gi, (word) => word.toUpperCase())
    .replace(/^./, (letter) => letter.toUpperCase())
}

function valueText(value: unknown): string {
  if (value == null) return ""
  if (Array.isArray(value)) return value.map(valueText).join("\n")
  return typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)
}

function downloadCsv(name: string, text: string) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `${name.replace(/[^\p{L}\p{N}._-]+/gu, "_").slice(0, 120) || "value"}.csv`
  document.body.append(link)
  try {
    link.click()
  } finally {
    link.remove()
    URL.revokeObjectURL(url)
  }
}

function csvRows(
  value: unknown,
  path: string[],
  translate: (key: string) => string,
  contractLabels: Record<string, string>,
): string[][] {
  if (isField(value)) return [[path.join(" / "), valueText(value)]]
  return Object.entries(value as object).flatMap(([key, entry]) =>
    csvRows(
      entry,
      [...path, Array.isArray(value) ? String(Number(key) + 1) : fieldLabel(key, translate, contractLabels)],
      translate,
      contractLabels,
    ),
  )
}

function isTableRow(row: unknown) {
  return (isObject(row) || Array.isArray(row)) && Object.values(row).every(isField)
}

function tableColumns(values: unknown[], columns?: unknown) {
  if (Array.isArray(columns))
    return columns.map((column, index) => ({
      key: isObject(column) ? String(column.key ?? column.name ?? index) : String(column),
      label: isObject(column) ? String(column.label ?? column.name ?? column.key ?? index + 1) : String(column),
    }))
  return [...new Set(values.flatMap((row) => Object.keys(row as object)))].map((key) => ({
    key,
    label: values.every(Array.isArray) ? String(Number(key) + 1) : key,
  }))
}

function CsvDownload(props: { value: unknown; name: string }) {
  const language = useLanguage()
  const fieldLabels = useContext(FieldLabels)
  const [state, setState] = createStore({ failed: false })
  const download = () => {
    try {
      let rows = [
        [
          language.t("cowork.accounting.extractions.column.field"),
          language.t("cowork.accounting.extractions.column.value"),
        ],
        ...csvRows(
          props.value,
          isField(props.value) ? [fieldLabel(props.name, language.t, fieldLabels)] : [],
          language.t,
          fieldLabels,
        ),
      ]
      const table = isObject(props.value) && Array.isArray(props.value.rows) ? props.value : undefined
      const values = table ? (table.rows as unknown[]) : props.value
      if (Array.isArray(values) && values.length > 0 && values.every(isTableRow)) {
        const columns = tableColumns(values, table?.columns ?? table?.headers)
        rows = [
          columns.map((column) => fieldLabel(column.label, language.t, fieldLabels)),
          ...values.map((row) =>
            columns.map((column, index) =>
              valueText(Array.isArray(row) ? row[index] : (row as Record<string, unknown>)[column.key]),
            ),
          ),
        ]
      }
      const csv = rows.map((row) => row.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(",")).join("\r\n")
      downloadCsv(props.name, csv)
      setState("failed", false)
    } catch {
      setState("failed", true)
    }
  }
  return (
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="rounded p-1.5 hover:bg-v2-overlay-simple-overlay-hover"
        aria-label={language.t("cowork.accounting.table.downloadCsv")}
        title={language.t("cowork.accounting.table.downloadCsv")}
        onClick={download}
      >
        <IconV2 name="download" size="small" />
      </button>
      <Show when={state.failed}>
        <span role="alert">{language.t("common.requestFailed")}</span>
      </Show>
    </div>
  )
}

function FieldValue(props: { value: unknown }) {
  const language = useLanguage()
  const [state, setState] = createStore({ copied: false, failed: false })
  let timer: ReturnType<typeof setTimeout> | undefined
  let alive = true
  onCleanup(() => {
    alive = false
    clearTimeout(timer)
  })
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(valueText(props.value))
      if (!alive) return
      setState({ copied: true, failed: false })
      clearTimeout(timer)
      timer = setTimeout(() => setState("copied", false), 1500)
    } catch {
      if (alive) setState({ failed: true, copied: false })
    }
  }
  return (
    <div class="extracted-field-value flex min-w-0 items-start justify-between gap-3">
      <span class="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        {props.value == null
          ? "—"
          : typeof props.value === "boolean"
            ? language.t(props.value ? "common.yes" : "common.no")
            : formatDate(valueText(props.value))}
      </span>
      <div class="extracted-field-actions flex shrink-0 items-center gap-1">
        <button
          type="button"
          disabled={props.value == null}
          onClick={() => void copy()}
          class="rounded p-1.5 hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-40"
          title={language.t("cowork.accounting.documents.copyCell")}
          aria-label={language.t("cowork.accounting.documents.copyCell")}
        >
          <IconV2 name="outline-copy" size="small" />
        </button>
        <Show when={state.copied}>
          <span role="status">{language.t("session.share.copy.copied")}</span>
        </Show>
        <Show when={state.failed}>
          <span role="alert">{language.t("common.requestFailed")}</span>
        </Show>
      </div>
    </div>
  )
}

export function AccountingRecordFacts(props: { records: Record<string, unknown>[] }) {
  const language = useLanguage()
  const fieldLabels = useContext(FieldLabels)
  const [state, setState] = createStore({ count: 20 })
  return (
    <section class="space-y-4" aria-label={language.t("cowork.accounting.extractions.accountingFacts")}>
      <div class="flex items-center justify-between gap-3">
        <h3 class="font-semibold">{language.t("cowork.accounting.extractions.accountingFacts")}</h3>
        <CsvDownload value={props.records} name="accounting-facts" />
      </div>
      <For each={props.records.slice(0, state.count)}>
        {(record) => (
          <article class="space-y-3 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3">
            <Show when={record.event_type || record.record_id}>
              <h4 class="break-words font-medium">
                {fieldLabel(String(record.event_type || record.record_id), language.t, fieldLabels)}
              </h4>
            </Show>
            <ExtractedFields value={record.facts ?? record} name="facts" />
          </article>
        )}
      </For>
      <Show when={state.count < props.records.length}>
        <button
          type="button"
          class="rounded border border-v2-border-border-base px-3 py-2"
          onClick={() => setState("count", state.count + 20)}
        >
          {language.t("common.loadMore")}
        </button>
      </Show>
    </section>
  )
}

export function ExtractedFields(props: {
  value: unknown
  name?: string
  depth?: number
  discloseSupporting?: boolean
}) {
  const language = useLanguage()
  const fieldLabels = useContext(FieldLabels)
  const name = props.name ?? "fields"
  const value = props.value
  const depth = props.depth ?? 0
  if (isField(value) || depth > 12) return <FieldValue value={value} />
  if (Array.isArray(value))
    return <RecordTable values={value} name={name} depth={depth} discloseSupporting={props.discloseSupporting} />
  if (!isObject(value)) return null
  if (Array.isArray(value.rows)) {
    const metadata = Object.fromEntries(
      Object.entries(value).filter(([key]) => !["rows", "columns", "headers"].includes(key)),
    )
    return (
      <div class="space-y-3">
        <Show when={Object.keys(metadata).length > 0}>
          <ExtractedFields
            value={metadata}
            name={name}
            depth={depth + 1}
            discloseSupporting={props.discloseSupporting}
          />
        </Show>
        <RecordTable
          values={value.rows}
          columns={value.columns ?? value.headers}
          name={name}
          depth={depth}
          discloseSupporting={props.discloseSupporting}
        />
      </div>
    )
  }
  const supporting = (key: string) => props.discloseSupporting !== false && isSupportingField(key)
  const fields = Object.entries(value).filter(([key, entry]) => !supporting(key) && isField(entry))
  const groups = Object.entries(value).filter(([key, entry]) => supporting(key) || !isField(entry))
  if (!fields.length && !groups.length) return <p>{language.t("cowork.accounting.documents.noFilterValues")}</p>
  return (
    <div class="space-y-4">
      <Show when={fields.length > 0}>
        <div class="extracted-field-table overflow-auto rounded-md border border-v2-border-border-base">
          <table class="w-full table-fixed border-collapse text-left text-[12px]">
            <thead class="bg-v2-background-bg-layer-02">
              <tr>
                <th scope="col" class="w-[32%] px-3 py-2 font-medium">
                  {language.t("cowork.accounting.extractions.column.field")}
                </th>
                <th scope="col" class="px-3 py-2 font-medium">
                  <div class="flex items-center justify-between gap-2">
                    {language.t("cowork.accounting.extractions.column.value")}
                    <CsvDownload value={Object.fromEntries(fields)} name={name} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <For each={fields}>
                {([key, entry]) => (
                  <tr>
                    <th
                      scope="row"
                      class="break-words border-t border-v2-border-border-base px-3 py-2 align-top font-medium"
                    >
                      {fieldLabel(key, language.t, fieldLabels)}
                    </th>
                    <td class="border-t border-v2-border-border-base px-3 py-2 align-top">
                      <FieldValue value={entry} />
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
      <For each={groups}>
        {([key, entry]) => (
          <Show
            when={supporting(key)}
            fallback={
              <section class="extracted-field-group min-w-0 space-y-2">
                <div class="flex items-center justify-between gap-3">
                  <h4 class="break-words font-medium">{fieldLabel(key, language.t, fieldLabels)}</h4>
                  <CsvDownload value={entry} name={key} />
                </div>
                <ExtractedFields
                  value={entry}
                  name={key}
                  depth={depth + 1}
                  discloseSupporting={props.discloseSupporting}
                />
              </section>
            }
          >
            <details class="min-w-0 rounded-md border border-v2-border-border-base p-3">
              <summary class="cursor-pointer font-medium">{fieldLabel(key, language.t, fieldLabels)}</summary>
              <div class="mt-3 space-y-2">
                <div class="flex justify-end">
                  <CsvDownload value={entry} name={key} />
                </div>
                <ExtractedFields value={entry} name={key} depth={depth + 1} discloseSupporting={false} />
              </div>
            </details>
          </Show>
        )}
      </For>
    </div>
  )
}

function RecordTable(props: {
  values: unknown[]
  columns?: unknown
  name: string
  depth: number
  discloseSupporting?: boolean
}) {
  const language = useLanguage()
  const fieldLabels = useContext(FieldLabels)
  const [state, setState] = createStore({ count: 50 })
  const visible = () => props.values.slice(0, state.count)
  const simple = () => props.values.every(isTableRow)
  const columns = createMemo(() => (simple() ? tableColumns(props.values, props.columns) : []))
  return (
    <div class="space-y-3">
      <Show
        when={simple()}
        fallback={
          <For each={visible()}>
            {(row, index) => (
              <section class="extracted-record-card space-y-2 rounded-md border border-v2-border-border-base p-3">
                <h5 class="font-medium">{index() + 1}</h5>
                <ExtractedFields
                  value={row}
                  name={`${props.name}-${index() + 1}`}
                  depth={props.depth + 1}
                  discloseSupporting={props.discloseSupporting}
                />
              </section>
            )}
          </For>
        }
      >
        <div class="extracted-record-table max-h-[560px] overflow-auto rounded-md border border-v2-border-border-base">
          <table class="w-full border-collapse text-left text-[12px]">
            <thead class="sticky top-0 bg-v2-background-bg-layer-02">
              <tr>
                <For each={columns()}>
                  {(column) => (
                    <th scope="col" class="px-3 py-2 font-medium">
                      {fieldLabel(column.label, language.t, fieldLabels)}
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={visible()}>
                {(row) => (
                  <tr>
                    <For each={columns()}>
                      {(column, index) => (
                        <td class="min-w-36 border-t border-v2-border-border-base px-3 py-2 align-top">
                          <Show
                            when={props.discloseSupporting !== false && isSupportingField(column.key)}
                            fallback={
                              <FieldValue
                                value={Array.isArray(row) ? row[index()] : (row as Record<string, unknown>)[column.key]}
                              />
                            }
                          >
                            <details>
                              <summary class="cursor-pointer text-v2-text-text-muted">
                                {language.t("cowork.enterprise.audit.viewDetails")}
                              </summary>
                              <div class="mt-2">
                                <FieldValue
                                  value={
                                    Array.isArray(row) ? row[index()] : (row as Record<string, unknown>)[column.key]
                                  }
                                />
                              </div>
                            </details>
                          </Show>
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
      <Show when={state.count < props.values.length}>
        <button
          type="button"
          class="rounded border border-v2-border-border-base px-3 py-2"
          onClick={() => setState("count", state.count + 50)}
        >
          {language.t("common.loadMore")}
        </button>
      </Show>
    </div>
  )
}
